import { Injectable, ɵgetDirectives as getDirectives } from '@angular/core';
import { NgControl } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class GlobalClearableService {
  init(): void {
    if (typeof document === 'undefined') return;

    this.processNode(document.body);

    new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node instanceof Element) this.processNode(node);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  private processNode(root: Element): void {
    if (root.matches?.('mat-form-field')) this.attach(root);
    root.querySelectorAll('mat-form-field').forEach((f) => this.attach(f));
  }

  private isFilterContext(field: Element): boolean {
    if (field.closest('th[mat-header-cell]')) return true;
    if (field.closest('[data-filter-form]')) return true;
    return false;
  }

  private attach(field: Element): void {
    if (!this.isFilterContext(field)) return;
    if (field.hasAttribute('data-gclear') || field.hasAttribute('data-no-clearable')) return;
    if (field.querySelector('app-clearable-btn')) return;

    const matSelect = field.querySelector('mat-select');
    if (matSelect) {
      this.attachSelect(field, matSelect);
      return;
    }

    const input = this.resolveInput(field);
    if (!input) return;

    field.setAttribute('data-gclear', '');

    const btn = this.createBtn();
    this.insertSuffix(field, btn);

    setTimeout(() => {
      btn.style.visibility = input.value ? 'visible' : 'hidden';
    }, 0);

    input.addEventListener('input', () => {
      btn.style.visibility = input.value ? 'visible' : 'hidden';
    });

    const proto = input instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    const nativeProp = Object.getOwnPropertyDescriptor(proto, 'value')!;
    Object.defineProperty(input, 'value', {
      get() {
        return nativeProp.get!.call(this);
      },
      set(v: string) {
        nativeProp.set!.call(this, v);
        btn.style.visibility = v ? 'visible' : 'hidden';
      },
      configurable: true,
    });

    if (field.closest('th[mat-header-cell]') || field.closest('form[data-filter-form]')) {
      input.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') {
          e.preventDefault();
          this.triggerSearch(input);
        }
      });
    }

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      btn.style.visibility = 'hidden';
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.focus();
      this.triggerSearch(input);
    });
  }

  private attachSelect(field: Element, matSelectEl: Element): void {
    field.setAttribute('data-gclear', '');

    const btn = this.createBtn();
    btn.style.visibility = 'visible';
    btn.style.display = 'none';
    this.insertSuffix(field, btn);

    const hasValue = (): boolean => !!matSelectEl.querySelector('.mat-mdc-select-value-text')?.textContent?.trim();
    const toggle = (show: boolean) => {
      btn.style.display = show ? '' : 'none';
    };

    setTimeout(() => toggle(hasValue()), 0);

    const valueEl = matSelectEl.querySelector('.mat-mdc-select-value');
    if (valueEl) {
      new MutationObserver(() => toggle(hasValue())).observe(valueEl, { childList: true, subtree: true, characterData: true });
    }

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      toggle(false);

      const ngControl = getDirectives(matSelectEl).find((d): d is NgControl => d instanceof NgControl);
      ngControl?.control?.reset(null);

      this.triggerSearch(matSelectEl as HTMLElement);
    });
  }

  private resolveInput(field: Element): HTMLInputElement | HTMLTextAreaElement | null {
    if (field.querySelector('mat-select')) return null;

    const el = field.querySelector<HTMLInputElement>('input:not([type="password"]):not([type="hidden"])') ?? field.querySelector<HTMLTextAreaElement>('textarea');

    if (!el) return null;
    if (el.disabled || el.readOnly) return null;
    if (el.classList.contains('mat-mdc-chip-input')) return null;
    if (el.closest('mat-date-range-input')) return null;
    if (el.classList.contains('mat-datepicker-input') || el.classList.contains('mat-start-date') || el.classList.contains('mat-end-date')) return null;

    return el;
  }

  private createBtn(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'app-gclear-btn';
    btn.setAttribute('tabindex', '-1');
    btn.setAttribute('aria-label', 'Clear');
    btn.innerHTML = `<span class="material-icons">close</span>`;
    btn.style.visibility = 'hidden';

    const tooltip = document.createElement('div');
    tooltip.className = 'app-gclear-tooltip';
    tooltip.textContent = 'Clear';

    btn.addEventListener('mouseenter', () => {
      const rect = btn.getBoundingClientRect();
      tooltip.style.left = `${rect.left + rect.width / 2}px`;
      tooltip.style.top = `${rect.top}px`;
      document.body.appendChild(tooltip);
    });

    btn.addEventListener('mouseleave', () => tooltip.remove());
    btn.addEventListener('click', () => tooltip.remove());

    // Prevent input blur on click so Angular's DefaultValueAccessor doesn't
    // restore the old model value to the DOM before our clear runs.
    btn.addEventListener('mousedown', (e) => e.preventDefault());

    return btn;
  }

  private triggerSearch(input: HTMLElement): void {
    const form = input.closest('form[data-filter-form]');
    if (form) {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      return;
    }
    if (input.closest('th[mat-header-cell]')) {
      const table = input.closest('table');
      table?.dispatchEvent(new CustomEvent('appFilterSearch', { bubbles: true }));
    }
  }

  private insertSuffix(field: Element, btn: HTMLElement): void {
    const existing = field.querySelector<HTMLElement>('.mat-mdc-form-field-icon-suffix, .mat-mdc-form-field-text-suffix');
    if (existing) {
      existing.insertBefore(btn, existing.firstChild);
      return;
    }
    const flex = field.querySelector<HTMLElement>('.mat-mdc-form-field-flex');
    if (!flex) return;
    const suffix = document.createElement('div');
    suffix.className = 'mat-mdc-form-field-icon-suffix';
    flex.appendChild(suffix);
    suffix.appendChild(btn);
  }
}
